// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { isEmpty, isDefined, htmlEntities } from '@automator/utilities';

// Import style for web component
import styles from './web-component-style.scss';

// Get translatable strings
const strings = UncannyAutomatorBackend.logs.components.logItemFilterBlock.i18n;

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-log-filter-condition' )
export class AutomatorLogItemFilterCondition extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	// Properties
	@property( { type: String, attribute: 'condition-id' } ) accessor id;

	@property( { type: String, attribute: 'condition-code' } ) accessor code;

	@property( { type: String, attribute: 'integration-code' } ) accessor integrationCode;

	@property( { type: String, attribute: 'status-id' } ) accessor statusID;

	@property( { type: Boolean, attribute: 'is-open' } ) accessor isOpen = false;

	@property( { type: String, attribute: false } ) accessor resultMessage = '';

	@property( { type: String, attribute: false } ) accessor titleHTML = '';

	@property( { type: Array, attribute: false } ) accessor fields = [];

	// eslint-disable-next-line require-jsdoc
	render() {
		return html`
			<div 
				class="condition condition--${ this.statusID }"
			>
				${ this._templateIcon }
				${ this._templateSentence }
				${ this._templateStatus }
				${ this._templateOpenSidebarButton }
			</div>
		`;
	}

	/**
	 * Keeps the sidebar up to date
	 * Called before update() to compute values needed during the update.
	 * We'll use this method to keep the sidebar up to date when one the reactive properties of the action change
	 *
	 * @param {Map} changedProperties The properties that changed
	 */
	willUpdate( changedProperties ) {
		// Check if the property that changed was .isOpen
		// If that's the case, WE DON'T NEED TO DO THIS HERE AGAIN
		// When the property .isOpen changes, `.requestSidebar()` is invoked, which
		// is already sending the request to the main component to update the sidebar data
		if ( changedProperties.has( 'isOpen' ) ) {
			return;
		}

		// Check if it's open
		if ( ! this.isOpen ) {
			return;
		}

		// Request sidebar update
		this.updateSidebar();
	}

	/**
	 * Opens the sidebar to show more data about this item
	 */
	requestSidebar() {
		if ( this.isOpen ) {
			// Close it
			this.dispatchEvent(
				new CustomEvent( 'uap-log-request-sidebar', {
					bubbles: true,
					composed: true,
					detail: {
						action: 'close',
					}
				} )
			);

			return;
		}

		this.updateSidebar();
	}

	/**
	 * Updates the sidebar data
	 */
	updateSidebar() {
		this.dispatchEvent(
			new CustomEvent( 'uap-log-request-sidebar', {
				bubbles: true,
				composed: true,
				detail: {
					action: 'open',
					sourceID: `condition-${ this.id }`,
					content: this.sidebarContent
				}
			} )
		);
	}

	/**
	 * Creates the object passed to the sidebar
	 *
	 * @return {Object} The object passed to the sidebar
	 */
	get sidebarContent() {
		return {
			title: strings.sidebarTitle,
			summary: this.sidebarSummary,
			runs: this.sidebarRuns
		}
	}

	/**
	 * Creates the summary for the sidebar
	 *
	 * @return {Object} Summary for the sidebar
	 */
	get sidebarSummary() {
		// Summary items
		const items = [];

		// Add status
		items.push( {
			label: strings.summary.status,
			value: html`<uap-log-status status-id="condition-${ this.statusID }"></uap-log-status>`
		} );

		// Check if there a note
		if ( ! isEmpty( this.resultMessage ) ) {
			items.push( {
				label: strings.summary.notes,
				value: htmlEntities.decode( this.resultMessage )
			} );
		}

		return {
			title: html`
				<uap-log-integration-icon integration-id="${ this.integrationCode }" show-tooltip></uap-log-integration-icon>
				<uap-log-sentence .sentence="${ this.titleHTML }"></uap-log-sentence>
			`,
			items: items
		}
	}

	/**
	 * Creates the array for the sidebar to render the condition fields
	 * Conditions don't have "Runs", but the sidebar expects an array with instances, so we create a single run and
	 * send the fields as properties
	 *
	 * @return {Array} The array of runs for the sidebar
	 */
	get sidebarRuns() {
		return [
			{
				title: strings.properties,
				properties: this.fieldsForSidebar
			}
		];
	}

	/**
	 * Converts fields into properties for the sidebar
	 *
	 * @return {Array} where each element is a field of the condition, prepared for the sidebar
	 */
	get fieldsForSidebar() {
		// Prepare fields for sidebar
		const fieldsSidebar = [];

		this.fields.forEach( field => {
			// Get the value of the field that'll show in the sidebar
			// Actions will have a `parsed` property, which will contain the value of the tokens
			// instead of the token IDs
			let fieldValue = _uo.utility.isDefined( field.value.parsed ) ? field.value.parsed : field.value.raw;

			// Selects will also contain a `readable` property, which will contain the text of the option
			if ( field.type !== 'file' && ! isEmpty( field.value.readable ) ) {
				fieldValue = `${ fieldValue } (${ field.value.readable })`;
			}

			fieldsSidebar.push( {
				type:  field.type,
				label: field.label,
				value: fieldValue,
				attributes: ! isEmpty( field.attributes ) ? field.attributes : {}
			} );
		} );

		return fieldsSidebar;
	}

	/**
	 * Returns the template that renders the integration icon
	 *
	 * @return {TemplateResult} The template of the integration icon
	 */
	get _templateIcon() {
		return html`
			<div class="condition__icon">
				<uo-icon integration="${ this.integrationCode }" show-tooltip></uo-icon>
			</div>
		`;
	}

	/**
	 * Returns the template that renders the condition sentence
	 *
	 * @return {TemplateResult} The template of the condition sentence
	 */
	get _templateSentence() {
		return html`
			<div class="condition__sentence">
				<uap-log-sentence .sentence="${ this.titleHTML }"></uap-log-sentence>
			</div>
		`;
	}

	/**
	 * Returns the template that shows the condition status (not evaluated, met, not met)
	 *
	 * @return {TemplateResult} The template of the condition status
	 */
	get _templateStatus() {
		return html`
			<div class="condition__status">
				<uap-log-status status-id="condition-${ this.statusID }"></uap-log-status>
			</div>
		`;
	}

	/**
	 * Returns the template of the button that requests the sidebar
	 *
	 * @return {TemplateResult|undefined} The template of the button that requests the sidebar
	 */
	get _templateOpenSidebarButton() {
		// Check if we shouldn't add the button at all
		if ( [ 'not-evaluated' ].includes( this.statusID ) ) {
			return;
		}

		return html`
			<uo-tooltip class="condition__toggle-sidebar">
				${ ! this.isOpen ? strings.openInSidebar : strings.closeSidebar }

				<uo-button 
					slot="target"
					color="transparent" 
					@click=${ this.requestSidebar }
					size="small"
				>
					<uo-icon id="${ this.isOpen ? 'angle-left' : 'angle-right' }"></uo-icon>
				</uo-button>
			</uo-tooltip>
			
		`;
	}
}